import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Breadcrumb } from '../components/Breadcrumb';
import { BulkActions } from '../components/admin/AdminActions';
import { InquiryManagementActions } from '../components/admin/InquiryManagementActions';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LIST_INQUIRIES } from '../graphql/inquiries';
import type { InquiryStatus, ListInquiriesResponse } from '../types/inquiry';
import { format } from 'date-fns';
import { canPerformAction } from '../utils/permissions';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

type SortField = 'name' | 'leadScore' | 'status' | 'incidentType' | 'createdAt';
type SortDirection = 'asc' | 'desc';
type ViewDensity = 'compact' | 'comfortable' | 'spacious';

export function InquiryQueue() {
  const navigate = useNavigate();
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'ALL'>('ALL');
  const [sourceFilter, setSourceFilter] = useState<'ALL' | 'FORM' | 'EMAIL'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [incidentTypeFilter, setIncidentTypeFilter] = useState<string>('');
  const [leadScoreFilter, setLeadScoreFilter] = useState<string>('all');
  const [hasAccountFilter, setHasAccountFilter] = useState<'all' | 'yes' | 'no'>('all');
  
  // Table controls
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [viewDensity, setViewDensity] = useState<ViewDensity>('comfortable');
  const [selectedInquiries, setSelectedInquiries] = useState<Set<string>>(new Set());
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const handleBulkDelete = async () => {
    if (!canPerformAction(user?.role, 'inquiries', 'delete')) {
      alert('You do not have permission to delete inquiries.');
      return;
    }
    
    try {
      // TODO: Implement bulk delete mutation
      console.log('Bulk deleting inquiries:', Array.from(selectedInquiries));
      setSelectedInquiries(new Set());
      alert(`${selectedInquiries.size} inquiries deleted successfully`);
      refetch();
    } catch (error) {
      console.error('Error bulk deleting inquiries:', error);
      alert('Error deleting inquiries');
    }
  };

  const handleBulkExport = () => {
    // Get all filtered inquiries (not just current page)
    let filteredInquiries = inquiries.filter((inquiry: any) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          inquiry.firstName.toLowerCase().includes(query) ||
          inquiry.lastName.toLowerCase().includes(query) ||
          inquiry.email.toLowerCase().includes(query) ||
          inquiry.phoneNumber.includes(query) ||
          inquiry.incidentType.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      if (statusFilter !== 'ALL' && inquiry.status !== statusFilter) return false;
      if (sourceFilter !== 'ALL' && inquiry.source !== sourceFilter) return false;
      if (incidentTypeFilter && inquiry.incidentType !== incidentTypeFilter) return false;
      
      if (leadScoreFilter !== 'all') {
        if (leadScoreFilter === 'high' && inquiry.leadScore < 80) return false;
        if (leadScoreFilter === 'medium' && (inquiry.leadScore < 60 || inquiry.leadScore >= 80)) return false;
        if (leadScoreFilter === 'low' && inquiry.leadScore >= 60) return false;
      }
      
      return true;
    });

    const selectedData = filteredInquiries.filter(i => selectedInquiries.has(i.id));
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Incident Type', 'Source', 'Lead Score', 'Status', 'Created Date'].join(','),
      ...selectedData.map(i => [
        `"${i.firstName} ${i.lastName}"`,
        i.email || '',
        i.phoneNumber || '',
        i.incidentType,
        i.source || 'FORM',
        i.leadScore,
        i.status,
        format(new Date(i.createdAt), 'yyyy-MM-dd')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selected-inquiries-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedInquiries(new Set(paginatedInquiries.map((i: any) => i.id)));
    } else {
      setSelectedInquiries(new Set());
    }
  };

  const handleSelectInquiry = (inquiryId: string, checked: boolean) => {
    const newSelected = new Set(selectedInquiries);
    if (checked) {
      newSelected.add(inquiryId);
    } else {
      newSelected.delete(inquiryId);
    }
    setSelectedInquiries(newSelected);
  };

  const { data: userData, loading: userLoading } = useQuery<{ getCurrentUser: { id: string; email: string; firstName: string; lastName: string; role: string } }>(GET_CURRENT_USER);
  const { data, loading, error, refetch } = useQuery<ListInquiriesResponse>(LIST_INQUIRIES, {
    variables: { limit: 1000 },
    fetchPolicy: 'network-only',
  });

  const user = userData?.getCurrentUser;
  const inquiries = data?.listInquiries?.items || [];

  // Get unique incident types for filter
  const incidentTypes = useMemo(() => {
    const types = new Set<string>(inquiries.map((i: any) => i.incidentType));
    return Array.from(types).sort();
  }, [inquiries]);

  // Filtering, sorting, and pagination
  const { paginatedInquiries, totalPages, totalFiltered, stats } = useMemo(() => {
    let filtered = inquiries.filter((inquiry: any) => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          inquiry.firstName.toLowerCase().includes(query) ||
          inquiry.lastName.toLowerCase().includes(query) ||
          inquiry.email.toLowerCase().includes(query) ||
          inquiry.phoneNumber.includes(query) ||
          inquiry.incidentType.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (statusFilter !== 'ALL' && inquiry.status !== statusFilter) return false;
      
      // Source filter
      if (sourceFilter !== 'ALL' && inquiry.source !== sourceFilter) return false;
      
      // Incident type filter
      if (incidentTypeFilter && inquiry.incidentType !== incidentTypeFilter) return false;
      
      // Lead score filter
      if (leadScoreFilter !== 'all') {
        if (leadScoreFilter === 'high' && inquiry.leadScore < 80) return false;
        if (leadScoreFilter === 'medium' && (inquiry.leadScore < 60 || inquiry.leadScore >= 80)) return false;
        if (leadScoreFilter === 'low' && inquiry.leadScore >= 60) return false;
      }
      
      // Has account filter
      if (hasAccountFilter !== 'all') {
        if (hasAccountFilter === 'yes' && !inquiry.userId) return false;
        if (hasAccountFilter === 'no' && inquiry.userId) return false;
      }
      
      return true;
    });

    // Sort
    filtered.sort((a: any, b: any) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'name':
          aVal = `${a.firstName} ${a.lastName}`.toLowerCase();
          bVal = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'leadScore':
          aVal = a.leadScore;
          bVal = b.leadScore;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'incidentType':
          aVal = a.incidentType;
          bVal = b.incidentType;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Calculate stats
    const highPriority = filtered.filter((i: any) => i.leadScore >= 80).length;
    const mediumPriority = filtered.filter((i: any) => i.leadScore >= 60 && i.leadScore < 80).length;
    const newInquiries = filtered.filter((i: any) => i.status === 'NEW').length;

    // Pagination
    const totalFiltered = filtered.length;
    const totalPages = Math.ceil(totalFiltered / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedInquiries = filtered.slice(startIndex, endIndex);

    return { 
      paginatedInquiries, 
      totalPages, 
      totalFiltered,
      stats: {
        total: inquiries.length,
        high: highPriority,
        medium: mediumPriority,
        new: newInquiries,
      }
    };
  }, [inquiries, searchQuery, statusFilter, sourceFilter, incidentTypeFilter, leadScoreFilter, hasAccountFilter, sortField, sortDirection, currentPage, pageSize]);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter: any) => (value: any) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('ALL');
    setSourceFilter('ALL');
    setIncidentTypeFilter('');
    setLeadScoreFilter('all');
    setHasAccountFilter('all');
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleExport = () => {
    // Export ALL filtered inquiries, not just current page
    const allFilteredInquiries = inquiries.filter((inquiry: any) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          inquiry.firstName.toLowerCase().includes(query) ||
          inquiry.lastName.toLowerCase().includes(query) ||
          inquiry.email.toLowerCase().includes(query) ||
          inquiry.phoneNumber.includes(query) ||
          inquiry.incidentType.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (statusFilter !== 'ALL' && inquiry.status !== statusFilter) return false;
      if (sourceFilter !== 'ALL' && inquiry.source !== sourceFilter) return false;
      if (incidentTypeFilter && inquiry.incidentType !== incidentTypeFilter) return false;
      if (leadScoreFilter !== 'all') {
        if (leadScoreFilter === 'high' && inquiry.leadScore < 80) return false;
        if (leadScoreFilter === 'medium' && (inquiry.leadScore < 60 || inquiry.leadScore >= 80)) return false;
        if (leadScoreFilter === 'low' && inquiry.leadScore >= 60) return false;
      }
      return true;
    });

    const csvContent = [
      ['Name', 'Email', 'Phone', 'Incident Type', 'Source', 'Lead Score', 'Status', 'Created'].join(','),
      ...allFilteredInquiries.map((i: any) => [
        `"${i.firstName} ${i.lastName}"`,
        i.email,
        i.phoneNumber,
        i.incidentType,
        i.source || 'FORM',
        i.leadScore,
        i.status,
        format(new Date(i.createdAt), 'yyyy-MM-dd'),
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inquiries-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const getLeadScoreColor = (score: number) => {
    if (score >= 80) return 'text-red-600 bg-red-50 border-red-200';
    if (score >= 60) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getLeadScorePriority = (score: number) => {
    if (score >= 80) return 'HIGH';
    if (score >= 60) return 'MEDIUM';
    return 'LOW';
  };

  const getStatusBadgeVariant = (status: InquiryStatus): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      NEW: 'info',
      REVIEWING: 'warning',
      ACCEPTED: 'success',
      REJECTED: 'default',
    };
    return variants[status] || 'default';
  };

  const densityClasses = {
    compact: 'py-2',
    comfortable: 'py-3',
    spacious: 'py-4',
  };

  if (userLoading || (loading && !data)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600">Error loading inquiries</h2>
          <p className="text-gray-600 mt-2">{error.message}</p>
          <Button onClick={() => refetch()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb />

        {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-full px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-aalaw-charcoal">Inquiry Queue</h1>
              <Badge variant="secondary" className="text-xs">
                {totalFiltered.toLocaleString()} {totalFiltered === 1 ? 'inquiry' : 'inquiries'}
                {totalFiltered !== inquiries.length && ` of ${inquiries.length.toLocaleString()} total`}
              </Badge>
              {totalFiltered > 0 && (
                <span className="text-xs text-gray-500">
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* View Density */}
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                {(['compact', 'comfortable', 'spacious'] as ViewDensity[]).map((density) => (
                  <button
                    key={density}
                    onClick={() => setViewDensity(density)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      viewDensity === density
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title={density.charAt(0).toUpperCase() + density.slice(1)}
                  >
                    {density === 'compact' && '━'}
                    {density === 'comfortable' && '≡'}
                    {density === 'spacious' && '☰'}
                  </button>
                ))}
              </div>
              
              {/* Page Size Selector */}
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 px-2 text-xs border border-gray-300 rounded-lg"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
              </select>
              
              <Button variant="outline" size="sm" onClick={handleExport}>
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full px-6 py-6">
        {/* Stats Bar */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Total Inquiries</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.high.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">High Priority</p>
                </div>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-orange-600">{stats.medium.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Medium Priority</p>
                </div>
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.new.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">New Inquiries</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters Bar */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-12 gap-3">
              {/* Search */}
              <div className="col-span-3">
                <Input
                  type="text"
                  placeholder="Search name, email, phone..."
                  value={searchQuery}
                  onChange={(e) => handleFilterChange(setSearchQuery)(e.target.value)}
                  className="w-full h-9 text-sm"
                />
              </div>

              {/* Status */}
              <div className="col-span-2">
                <select
                  value={statusFilter}
                  onChange={(e) => handleFilterChange(setStatusFilter)(e.target.value as InquiryStatus | 'ALL')}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="NEW">New</option>
                  <option value="REVIEWING">Reviewing</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              {/* Source */}
              <div className="col-span-2">
                <select
                  value={sourceFilter}
                  onChange={(e) => handleFilterChange(setSourceFilter)(e.target.value as 'ALL' | 'FORM' | 'EMAIL')}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="ALL">All Sources</option>
                  <option value="FORM">Form Submissions</option>
                  <option value="EMAIL">Email Inquiries</option>
                </select>
              </div>

              {/* Incident Type */}
              <div className="col-span-2">
                <select
                  value={incidentTypeFilter}
                  onChange={(e) => handleFilterChange(setIncidentTypeFilter)(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="">All Incident Types</option>
                  {incidentTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Lead Score */}
              <div className="col-span-1">
                <select
                  value={leadScoreFilter}
                  onChange={(e) => handleFilterChange(setLeadScoreFilter)(e.target.value)}
                  className="w-full h-9 px-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="all">All Scores</option>
                  <option value="high">High (80+)</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Has Account */}
              <div className="col-span-1">
                <select
                  value={hasAccountFilter}
                  onChange={(e) => handleFilterChange(setHasAccountFilter)(e.target.value as 'all' | 'yes' | 'no')}
                  className="w-full h-9 px-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="all">All Accounts</option>
                  <option value="yes">Has Account</option>
                  <option value="no">No Account</option>
                </select>
              </div>

              {/* Actions */}
              <div className="col-span-1 flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </Button>
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-aalaw-red"
                  title="Clear all filters"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        <BulkActions
          userRole={user?.role}
          resource="inquiries"
          selectedCount={selectedInquiries.size}
          onBulkEdit={() => console.log('Bulk edit inquiries')}
          onBulkDelete={handleBulkDelete}
          onBulkExport={handleBulkExport}
          className="mb-4"
        />

        {/* Inquiries Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedInquiries.size === paginatedInquiries.length && paginatedInquiries.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                    />
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Name
                      <SortIcon field="name" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                    Contact
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('incidentType')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Incident Type
                      <SortIcon field="incidentType" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('leadScore')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Lead Score
                      <SortIcon field="leadScore" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Status
                      <SortIcon field="status" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Created
                      <SortIcon field="createdAt" />
                    </button>
                  </th>
                  <th className="w-24 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedInquiries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12">
                      <p className="text-gray-500">No inquiries found</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {searchQuery || statusFilter !== 'ALL' || incidentTypeFilter || leadScoreFilter !== 'all'
                          ? 'Try adjusting your filters'
                          : 'New inquiries will appear here'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedInquiries.map((inquiry: any) => (
                    <tr
                      key={inquiry.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                    >
                      <td className={`px-4 ${densityClasses[viewDensity]}`} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedInquiries.has(inquiry.id)}
                          onChange={(e) => handleSelectInquiry(inquiry.id, e.target.checked)}
                          className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                        />
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            inquiry.leadScore >= 80 ? 'bg-red-600' : 
                            inquiry.leadScore >= 60 ? 'bg-orange-500' : 
                            'bg-gray-400'
                          }`}></div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">
                              {inquiry.firstName} {inquiry.lastName}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                inquiry.source === 'EMAIL' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {inquiry.source === 'EMAIL' ? '📧 Email' : '📝 Form'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <div className="text-sm">
                          <div className="text-gray-700">{inquiry.email}</div>
                          <div className="text-gray-500">{inquiry.phoneNumber}</div>
                        </div>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-700">{inquiry.incidentType}</span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getLeadScoreColor(inquiry.leadScore)}`}>
                            {inquiry.leadScore}
                          </span>
                          <span className="text-xs text-gray-500">
                            {getLeadScorePriority(inquiry.leadScore)}
                          </span>
                        </div>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <Badge variant={getStatusBadgeVariant(inquiry.status)} className="text-xs">
                          {inquiry.status}
                        </Badge>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-600">
                          {format(new Date(inquiry.createdAt), 'MMM d, yyyy')}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`} onClick={(e) => e.stopPropagation()}>
                        <InquiryManagementActions
                          inquiryId={inquiry.id}
                          inquiryName={`${inquiry.firstName} ${inquiry.lastName}`}
                          userRole={user?.role}
                          onView={() => navigate(`/inquiries/${inquiry.id}`)}
                          onRefresh={refetch}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalFiltered)} of {totalFiltered.toLocaleString()} inquiries
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((page, index) => (
                      typeof page === 'string' ? (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">{page}</span>
                      ) : (
                        <button
                          key={`page-${page}`}
                          onClick={() => goToPage(page)}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                            currentPage === page
                              ? 'bg-aalaw-red text-white font-semibold'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}
                  </div>
                  
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </main>
      </div>
    </div>
  );
}
